import { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Session, User } from "@supabase/supabase-js";
import { validateEmail, validatePassword } from "@/lib/input-validation";
import { setUser as setSentryUser } from "@/utils/sentry";

// Custom logger that only logs in development mode
const logger = {
  log: (message: string, ...args: any[]) => {
    if (import.meta.env.DEV) {
      console.log(message, ...args);
    }
  },
  error: (message: string, ...args: any[]) => {
    if (import.meta.env.DEV) {
      console.error(message, ...args);
    } else {
      // In production, log errors without sensitive data
      console.error('An error occurred with authentication');
    }
  }
};

interface AuthContextType {
  session: Session | null;
  user: User | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, firstName: string, lastName: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Session refresh functionality
  useEffect(() => {
    let refreshTimer: NodeJS.Timeout;

    const scheduleSessionRefresh = (session: Session) => {
      if (refreshTimer) {
        clearTimeout(refreshTimer);
      }

      // Calculate when to refresh the session (15 minutes before expiry)
      if (!session.expires_at) {
        logger.log("⚠️ Session has no expiry time, cannot schedule refresh");
        return;
      }
      
      const expiresAt = new Date(session.expires_at * 1000);
      const refreshAt = new Date(expiresAt.getTime() - 15 * 60 * 1000); // 15 minutes before expiry
      const now = new Date();
      const timeUntilRefresh = refreshAt.getTime() - now.getTime();

      // Only schedule if the refresh time is in the future
      if (timeUntilRefresh > 0) {
        logger.log(`⏰ Scheduling session refresh in ${Math.round(timeUntilRefresh / 1000 / 60)} minutes`);
        
        refreshTimer = setTimeout(async () => {
          // Prevent multiple simultaneous refresh attempts
          if (isRefreshing) {
            logger.log("🔄 Session refresh already in progress, skipping");
            return;
          }
          
          try {
            setIsRefreshing(true);
            logger.log("🔄 Auto-refreshing session...");
            const { data, error } = await supabase.auth.refreshSession();
            
            if (error) {
              logger.error("❌ Session refresh failed:", error);
              // Don't show user-facing error, just handle silently
              return;
            }
            
            if (data.session) {
              logger.log("✅ Session refreshed successfully");
              setSession(data.session);
              setUser(data.session.user);
              // Schedule the next refresh
              scheduleSessionRefresh(data.session);
            }
          } catch (error) {
            logger.error("❌ Unexpected error during session refresh:", error);
          } finally {
            setIsRefreshing(false);
          }
        }, timeUntilRefresh);
      } else {
        logger.log("⚠️ Session expires too soon, cannot schedule refresh");
      }
    };

    // Schedule refresh for current session
    if (session?.expires_at) {
      scheduleSessionRefresh(session);
    }

    return () => {
      if (refreshTimer) {
        clearTimeout(refreshTimer);
      }
    };
  }, [session]);

  useEffect(() => {
    logger.log("🔄 Initializing auth state...");
    
    // Get the current session
    const initSession = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          logger.error("Error getting session:", error);
          setLoading(false);
          return;
        }
        
        logger.log("📥 Initial session:", data.session ? "Found" : "None");
        setSession(data.session);
        setUser(data.session?.user ?? null);
      } catch (err) {
        logger.error("Failed to get session:", err);
      } finally {
        setLoading(false);
      }
    };
    
    initSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      logger.log("🔄 Auth state changed:", { event: _event, hasSession: !!session });
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Handle window focus to refresh session if needed
  useEffect(() => {
    const handleWindowFocus = async () => {
      // Only refresh if we have a session and it's been more than 5 minutes since last activity
      if (session?.expires_at && !isRefreshing) {
        const expiresAt = new Date(session.expires_at * 1000);
        const now = new Date();
        const timeUntilExpiry = expiresAt.getTime() - now.getTime();
        
        // If session expires within 30 minutes, refresh it proactively
        if (timeUntilExpiry > 0 && timeUntilExpiry < 30 * 60 * 1000) {
          try {
            setIsRefreshing(true);
            logger.log("🔄 Refreshing session on window focus...");
            const { data, error } = await supabase.auth.refreshSession();
            
            if (error) {
              logger.error("❌ Session refresh on focus failed:", error);
              return;
            }
            
            if (data.session) {
              logger.log("✅ Session refreshed on focus");
            }
          } catch (error) {
            logger.error("❌ Unexpected error during focus refresh:", error);
          } finally {
            setIsRefreshing(false);
          }
        }
      }
    };

    window.addEventListener('focus', handleWindowFocus);
    
    return () => {
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [session, isRefreshing]);

  // Effect to update Sentry with user info when auth state changes
  useEffect(() => {
    if (user) {
      // Set user info in Sentry when user is available
      setSentryUser({
        id: user.id,
        email: user.email,
        username: user.email?.split('@')[0], // Use part of email as username
      });
    } else {
      // Clear user from Sentry when signed out
      setSentryUser(null);
    }
  }, [user]);

  const signUp = async (email: string, password: string, firstName: string, lastName: string) => {
    logger.log("🔐 Starting signup process...");
    
    try {
      // Validate email
      const emailValidation = validateEmail(email);
      if (!emailValidation.valid) {
        throw new Error(emailValidation.message || "Invalid email");
      }
      
      // Email is valid but might have a suggestion - we use the original email
      if (emailValidation.suggestion) {
        // Show a suggestion toast but continue with the provided email
        toast({
          title: "Email suggestion",
          description: emailValidation.message,
          variant: "default",
        });
      }
      
      // Validate password
      const passwordValidation = validatePassword(password);
      if (!passwordValidation.valid) {
        throw new Error(passwordValidation.message || "Password doesn't meet security requirements");
      }
      
      const { data, error } = await supabase.auth.signUp({
        email, // Use the original email even if there was a suggestion
        password,
        options: {
          data: {
            first_name: firstName,
            last_name: lastName,
          },
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      
      if (error) {
        logger.error("❌ Supabase signup error:", error);
        throw error;
      }

      // Create or update profile record if signup was successful (upsert avoids 409 if profile already exists)
      if (data.user) {
        try {
          const profilePayload = {
            id: data.user.id,
            first_name: firstName,
            last_name: lastName,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            email_notifications: true,
            project_updates: true,
            marketing_emails: false,
            plan_key: 'free',
            content_used: 0,
            content_quota: null,
            account_state: 'on_free_tier',
            used_free_reddit_post: false,
            feature_flags: {},
          };
          const { error: profileError } = await supabase.from('profiles').upsert(profilePayload, {
            onConflict: 'id',
            ignoreDuplicates: false,
          });
          
          if (profileError) {
            logger.error("❌ Error creating/updating profile:", profileError);
            // We don't throw here to avoid blocking the signup process
          }
          
          // Send notification to Slack directly from the client
          // This follows the same pattern as the working Feedback function
          try {
            logger.log("📱 Sending registration notification to Slack for user:", data.user.id);
            
            // Call the edge function directly
            const { error: notificationError } = await supabase.functions.invoke("notify-slack-registration", {
              body: {
                id: data.user.id,
                email: email,
                created_at: new Date().toISOString()
              }
            });
            
            if (notificationError) {
              logger.error("❌ Error sending registration notification:", notificationError);
              // Don't block signup process if notification fails
            } else {
              logger.log("✅ Registration notification sent successfully");
            }
          } catch (notifyErr) {
            logger.error("❌ Unexpected error sending registration notification:", notifyErr);
            // Don't block signup process if notification fails
          }
        } catch (profileErr) {
          logger.error("❌ Unexpected error creating profile:", profileErr);
        }
      }

      // For new sign-ups, always set flag and direct to onboarding
      if (data.session) {
        // Set a flag to trigger onboarding
        localStorage.setItem("showOnboarding", "true");
        
        // Navigate to onboarding directly
        navigate("/onboarding");
        
        toast({
          title: "Welcome!",
          description: "Your account has been created successfully.",
        });
      }
    } catch (error: any) {
      logger.error("❌ Signup process error:", error);
      toast({
        title: "Error signing up",
        description: error.message,
        variant: "destructive",
      });
      throw error;
    }
  };

  const signIn = async (email: string, password: string) => {
    logger.log("🔐 Starting sign-in process...");
    try {
      // Validate email
      const emailValidation = validateEmail(email);
      if (!emailValidation.valid) {
        throw new Error(emailValidation.message || "Invalid email");
      }
      
      // Email is valid but might have a suggestion - we use the original email
      if (emailValidation.suggestion) {
        // Show a suggestion toast but continue with the provided email
        toast({
          title: "Email suggestion",
          description: emailValidation.message,
          variant: "default",
        });
      }
      
      // Basic password validation - just check it's not empty
      if (!password) {
        throw new Error("Password is required");
      }
      
      // Check minimum password length without doing full validation
      if (password.length < 8) {
        throw new Error("Password must be at least 8 characters");
      }
      
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      
      if (error) {
        logger.error("❌ Sign-in error:", error);
        throw error;
      }
      
      logger.log("✅ Sign-in successful:", data.session ? "Session created" : "No session");
      
      // Always navigate to projects page for sign-in (not onboarding)
      navigate("/projects");
      
      toast({
        title: "Welcome back!",
        description: "You have successfully signed in.",
      });
    } catch (error: any) {
      logger.error("❌ Sign-in error:", error);
      toast({
        title: "Error signing in",
        description: error.message || "Failed to sign in. Please try again.",
        variant: "destructive",
      });
      throw error;
    }
  };

  const signOut = async () => {
    try {
      logger.log("🚪 Signing out...");
      // Use local scope to only sign out from current session
      // This avoids the global scope 403 errors that occur when sessions are out of sync
      const { error } = await supabase.auth.signOut({ scope: 'local' });
      
      if (error) {
        // Handle the common 403 error that occurs when session is out of sync
        if (error.status === 403 || error.message?.includes("Session from session_id claim in JWT does not exist")) {
          logger.log("⚠️ Session already invalid on server, clearing local session");
          // Clear the local session manually when server says session doesn't exist
          // This is the recommended approach from Supabase support
        } else {
          logger.error("❌ Error signing out:", error);
          throw error;
        }
      }
      
      // Clear Sentry user context
      setSentryUser(null);
      
      // Clear any stored project IDs
      localStorage.removeItem("lastProjectId");
      
      // Clear any stored organization IDs
      localStorage.removeItem("selectedOrganizationId");
      
      // Clear any auth-related items from localStorage to ensure clean state
      // This helps resolve the 403 session mismatch issues
      const authKeys = Object.keys(localStorage).filter(key => 
        key.includes('supabase') || key.includes('auth') || key.startsWith('sb-')
      );
      authKeys.forEach(key => localStorage.removeItem(key));
      
      // Set local state to null to ensure UI updates
      setSession(null);
      setUser(null);
      
      // Redirect to home page
      navigate("/");
      
      logger.log("✅ Signed out successfully");
    } catch (error: any) {
      logger.error("❌ Unexpected error during sign-out:", error);
      
      // Even if there's an error, try to clear local state and redirect
      // This ensures the user can still "log out" from the UI perspective
      try {
        setSentryUser(null);
        localStorage.removeItem("lastProjectId");
        localStorage.removeItem("selectedOrganizationId");
        
        // Clear auth-related localStorage items
        const authKeys = Object.keys(localStorage).filter(key => 
          key.includes('supabase') || key.includes('auth') || key.startsWith('sb-')
        );
        authKeys.forEach(key => localStorage.removeItem(key));
        
        setSession(null);
        setUser(null);
        navigate("/");
        
        logger.log("✅ Forced local logout completed");
      } catch (cleanupError) {
        logger.error("❌ Error during cleanup:", cleanupError);
      }
      
      toast({
        title: "Signed out",
        description: "You have been signed out successfully.",
      });
    }
  };

  const resetPassword = async (email: string) => {
    try {
      logger.log("🔑 Initiating password reset for:", email);
      
      // Validate email
      const emailValidation = validateEmail(email);
      if (!emailValidation.valid) {
        throw new Error(emailValidation.message || "Invalid email");
      }
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/update-password`,
      });
      
      if (error) {
        logger.error("❌ Password reset error:", error);
        throw error;
      }
      
      logger.log("✅ Password reset email sent");
      
      toast({
        title: "Password reset initiated",
        description: "Check your email for a password reset link.",
      });
    } catch (error: any) {
      logger.error("❌ Password reset error:", error);
      toast({
        title: "Error resetting password",
        description: error.message,
        variant: "destructive",
      });
      throw error;
    }
  };

  const updatePassword = async (password: string) => {
    try {
      logger.log("🔑 Updating password...");
      
      // Validate password
      const passwordValidation = validatePassword(password);
      if (!passwordValidation.valid) {
        throw new Error(passwordValidation.message || "Password doesn't meet security requirements");
      }
      
      const { error } = await supabase.auth.updateUser({
        password,
      });
      
      if (error) {
        logger.error("❌ Password update error:", error);
        throw error;
      }
      
      logger.log("✅ Password updated successfully");
      
      // Navigate to projects or login page
      navigate("/projects");
      
      toast({
        title: "Password updated",
        description: "Your password has been updated successfully.",
      });
    } catch (error: any) {
      logger.error("❌ Password update error:", error);
      toast({
        title: "Error updating password",
        description: error.message,
        variant: "destructive",
      });
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{
      session,
      user,
      signIn,
      signUp,
      signOut,
      resetPassword,
      updatePassword,
      loading,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
